"use client";

import type React from "react";
import { useState, useEffect, useRef } from "react";

type PatternCoordinate = [number, number];

export interface PatternType {
  price: number;
  pattern: PatternCoordinate[][];
  winCount: number;
  patternClaimCount: number;
}

export interface PatternsData {
  [key: string]: PatternType;
}

interface CreatePatternsProps {
  drawPatterns: PatternsData;
}

const CreatePatterns: React.FC<CreatePatternsProps> = ({ drawPatterns }) => {
  const patternTypes = Object.keys(drawPatterns);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [useFixedHeight, setUseFixedHeight] = useState<boolean>(false);

  const [selectedPattern, setSelectedPattern] = useState<string>(
    patternTypes[0] || ""
  );

  const getMaxGridDimensions = () => {
    let maxRows = 0;
    let maxCols = 0;

    patternTypes.forEach((type) => {
      drawPatterns[type].pattern.forEach((subPattern) => {
        const maxPatternRow = Math.max(...subPattern.map(([row]) => row)) + 1;
        const maxPatternCol = Math.max(...subPattern.map(([, col]) => col)) + 1;
        maxRows = Math.max(maxRows, maxPatternRow);
        maxCols = Math.max(maxCols, maxPatternCol);
      });
    });
    return { rows: Math.max(maxRows), cols: Math.max(maxCols) };
  };

  const { rows, cols } = getMaxGridDimensions();

  const CELL_SIZE =
    cols !== 10
      ? window.innerWidth <= 640
        ? 20
        : window.innerWidth < 1024
          ? 32
          : 40
      : window.innerWidth <= 640
        ? 20
        : window.innerWidth < 1024
          ? 20
          : 35;
  const BLUE_STAR_SIZE =
    window.innerWidth < 640 ? { width: 4, height: 4 } : { width: 6, height: 6 };
  const marginBottom = window.innerWidth < 640 ? "8px" : "12px";
  const renderSubPatterns = (patternType: PatternType) => {
    return (
      <div
        className="flex flex-wrap content-start pr-2 sm:pr-4 overflow-y-auto custom-scrollbar"
        style={{
          height: useFixedHeight ? "480px" : `${containerHeight - 100}px`,
          paddingTop: "8px",
          paddingBottom: "24px",
          overflowY: "auto",
        }}
      >
        {patternType.pattern.map((subPattern, index) => (
          <div key={`${selectedPattern}-${index}`}>
            {renderPatternGrid({
              pattern: subPattern,
              rows,
              cols,
              CELL_SIZE,
              blueStarSize: BLUE_STAR_SIZE,
              img: "skyBlue",
              marginBottom: marginBottom,
              marginRight: marginBottom,
            })}
          </div>
        ))}
      </div>
    );
  };

  const [isScrollNeeded, setIsScrollNeeded] = useState(false);
  const tabContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkScrollNeeded = () => {
      if (tabContainerRef.current) {
        const container = tabContainerRef.current;
        const isOverflowing = container.scrollWidth > container.clientWidth;
        setIsScrollNeeded(isOverflowing);

        const arrows =
          container.parentElement?.querySelectorAll(".scroll-arrow");
        if (arrows && arrows.length > 0) {
          arrows[0].classList.toggle("hidden", !isOverflowing);
          arrows[1].classList.toggle("hidden", !isOverflowing);
        }
      }
    };

    checkScrollNeeded();
    window.addEventListener("resize", checkScrollNeeded);
    setTimeout(checkScrollNeeded, 100);
    return () => window.removeEventListener("resize", checkScrollNeeded);
  }, []);

  const formatPatternName = (name: string) => {
    const camelCaseFormatted = name.replace(/([A-Z])/g, " $1");

    const formattedName =
      camelCaseFormatted.charAt(0).toUpperCase() + camelCaseFormatted.slice(1);

    return formattedName;
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-grow relative w-full h-full   overflow-hidden justify-center"
      style={{
        marginTop: useFixedHeight ? "30px" : "0",
      }}
    >
      <img
        src="/assets/transparentBg.png"
        alt="transparentBg"
        className="w-[90%] max-w-[1200px] h-full4  overflow-hidden object-fill"
      />
      <div
        className="rounded-lg absolute w-[90%] max-w-[1200px] h-full"
        style={{
          boxShadow: "0px 4px 12px rgba(185, 181, 181, 0.2)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {selectedPattern && drawPatterns[selectedPattern] && (
          <div className="flex flex-col h-screen p-2 sm:p-3 relative overflow-hidden">
            <div className="relative flex-shrink-0">
              <div
                ref={tabContainerRef}
                className="flex overflow-x-auto scrollbar-none rounded-md no-scrollbar"
                style={{
                  padding: "8px",
                  backgroundColor: "#1B0A7F",
                  gap: "4px",
                  scrollBehavior: "smooth",
                  position: "relative",
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                  display: "flex",
                  width: "100%",
                }}
                onScroll={(e) => {
                  const container = e.currentTarget;
                  const arrows =
                    container.parentElement?.querySelectorAll(".scroll-arrow");
                  if (arrows && arrows.length > 0) {
                    arrows[0].classList.toggle(
                      "hidden",
                      !isScrollNeeded || container.scrollLeft <= 0
                    );
                    arrows[1].classList.toggle(
                      "hidden",
                      !isScrollNeeded ||
                      container.scrollLeft + container.clientWidth >=
                      container.scrollWidth
                    );
                  }
                }}
              >
                {patternTypes.map((typeName) => (
                  <div
                    key={typeName}
                    onClick={() => setSelectedPattern(typeName)}
                    className={`flex-shrink-0 px-4 py-2 sm:px-6 sm:py-3 text-white text-[14px] sm:text-[16px] flex justify-center font-semibold ${selectedPattern === typeName ? "rounded-lg" : ""
                      } cursor-pointer whitespace-nowrap`}
                    style={{
                      background:
                        selectedPattern === typeName
                          ? "linear-gradient(to right, #00BBE7, #3F8DDE)"
                          : "transparent",
                      transition: "all 0.2s ease",
                      textAlign: "center",
                      flex: !isScrollNeeded ? "1 1 0" : "0 0 auto",
                      minWidth: "120px",
                    }}
                  >
                    {formatPatternName(typeName)}
                  </div>
                ))}
              </div>

              <div
                className="scroll-arrow hidden absolute left-0 top-0 h-full flex items-center justify-center cursor-pointer z-10"
                style={{
                  background:
                    "linear-gradient(to right, rgba(12, 5, 51, 0.9), rgba(27, 10, 127, 0))",
                  width: "35px",
                }}
                onClick={(e) => {
                  const scrollContainer =
                    e.currentTarget.parentElement?.querySelector(
                      ".overflow-x-auto"
                    );
                  if (scrollContainer) {
                    scrollContainer.scrollBy({
                      left: -100,
                      behavior: "smooth",
                    });
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-white drop-shadow-lg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  style={{
                    filter: "drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.5))",
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </div>

              <div
                className="scroll-arrow absolute right-0 top-0 h-full flex items-center justify-center cursor-pointer z-10"
                style={{
                  background:
                    "linear-gradient(to left, rgba(12, 5, 51, 0.9), rgba(27, 10, 127, 0))",
                  width: "35px",
                }}
                onClick={(e) => {
                  const scrollContainer =
                    e.currentTarget.parentElement?.querySelector(
                      ".overflow-x-auto"
                    );
                  if (scrollContainer) {
                    scrollContainer.scrollBy({ left: 100, behavior: "smooth" });
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-white drop-shadow-lg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  style={{
                    filter: "drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.5))",
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>

            <div className="flex-grow pt-2 sm:pt-3 overflow-hidden flex flex-col">
              {renderSubPatterns(drawPatterns[selectedPattern])}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePatterns;

type starImg = "skyBlue" | "blue";

interface RenderPatternGridProps {
  pattern: PatternCoordinate[];
  rows: number;
  cols: number;
  CELL_SIZE: number;
  blueStarSize: {
    width: number;
    height: number;
  };
  img: starImg;
  marginBottom: any;
  marginRight: any;
  isPatternAmount?: any;
}

export const renderPatternGrid = ({
  pattern,
  rows,
  cols,
  CELL_SIZE,
  blueStarSize,
  img,
  marginBottom,
  marginRight,
  isPatternAmount
}: RenderPatternGridProps) => {
  const grid: boolean[][] = Array(rows)
    .fill(null)
    .map(() => Array(cols).fill(false));

  pattern.forEach(([row, col]) => {
    if (row >= 0 && row < rows && col >= 0 && col < cols) {
      grid[row][col] = true;
    }
  });

  const findImage =
    img === "blue" ? "/assets/normalBlueStar.png" : "/assets/bluStr.png";

  const gridStyles = isPatternAmount ? {
    width: `${cols * CELL_SIZE}px`,
    height: `${rows * CELL_SIZE}px`,
  } : {}
  const blockStyles = isPatternAmount ? {
    width: `${CELL_SIZE}px`,
    height: `${CELL_SIZE}px`,
  } : {}

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: "0",
        // width: `${cols * CELL_SIZE}px`,
        // height: `${rows * CELL_SIZE}px`,
        border: "2px solid rgba(0, 104, 5, 0.2)",
        backgroundColor: "#F3EEFD",
        borderRadius: "4px",
        overflow: "hidden",
        marginBottom: marginBottom,
        marginRight: marginRight,
        backgroundImage: "/assets/numBg.png",
        ...gridStyles
      }}
    >
      {grid.map((row, rowIndex) =>
        row.map((isMarked, colIndex) => (
          <div
            key={`${rowIndex}-${colIndex}`}
            className="border border-gray-100 md:w-10  md:h-10  w-[28px]  h-[28px]  rounded-md flex items-center justify-center"
            style={{
              padding: "1px",
              backgroundColor: "#F3EEFD",
              border: "1px solid #E0E0E6",
              ...blockStyles
            }}
          >
            {isMarked && (
              <img
                src={findImage}
                alt="Star"
                className={`w-${blueStarSize.width} h-${blueStarSize.height}`}
              />
            )}
          </div>
        ))
      )}
    </div>
  );
};
